"use client";

/**
 * Renders SVGs that have been included in the document as symbols.
 *
 * Intended to reference symbols located in icons.svg. Note: xlinkHref is deprecated (should be
 * replaced with href), but as of this writing still required for the icons to display in Safari.
 */

import React, { MouseEventHandler } from "react";

import styled, { css } from "styled-components";
import { positioningCSS, cssVal, getColor, flexCSS } from "../utils";
import iconIndex from "./iconsIndex";
import legacyNames from "./legacyNames";
import {
  ElementFlexProps,
  SpacingValue,
  ElementPositioningProps,
  ElementTransitionProps,
} from "../ComponentProps";

type IconProps = {
  id?: string;
  iconId: string;
  disabled?: boolean;
  coverParent?: boolean;
  fill?: string;
  onClick?: MouseEventHandler<SVGElement>;
  title?: string;
  size?: SpacingValue;
  width?: SpacingValue;
  height?: SpacingValue;
  viewBox?: string;
  style?: React.CSSProperties;
  className?: string;
};
type Props = IconProps & ElementPositioningProps & ElementFlexProps & ElementTransitionProps;

const coverStyle = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
  max-height: 100%;
  max-width: 100%;
`;

const IconSvg = styled.svg<Props>`
  cursor: ${props => getCursorStyle(props)};
  fill: ${props =>
    getColor(props.disabled ? "grayDark" : ((props.theme[props.fill] as string) ?? props.fill))};
  ${props => getSizeStyle(props)};
  ${props => (props.coverParent ? coverStyle : null)};
  user-select: none;
  ${positioningCSS}
  ${flexCSS}
  flex-shrink: 0;
`;

const getSizeStyle = ({ iconId, size, width, height }: Partial<Props>) => {
  let w = width || size,
    h = height || size;

  // Adjust one dimension (x) based on the alternate dimension (y)
  // pX: the provided dimension to adjust
  // x: the actual dimension of the icon
  // pY: the provided alternate dimension
  // y: the actual alternate dimension
  function adjustDimension(pX, x, pY, y) {
    if (pX) return pX;
    return pY ? Math.round(x * (pY / y)) : x;
  }

  if (!w || !h) {
    const dims = iconIndex[iconId];
    if (dims) {
      const [actualWidth, actualHeight] = dims.split("x").map(Number);
      w = adjustDimension(w, actualWidth, h, actualHeight);
      h = adjustDimension(h, actualHeight, w, actualWidth);
    } else {
      console.warn(`Icon id ${iconId} not found`);
    }
  }
  return {
    width: cssVal(w),
    height: cssVal(h),
  };
};

const getCursorStyle = ({ onClick, disabled }: Partial<IconProps>) => {
  if (disabled) return "not-allowed";
  if (onClick) return "pointer";
  return null;
};

function resolveIconId(
  id: string,
  { size, width }: { size?: SpacingValue; width?: SpacingValue }
): string {
  if (cssVal(size || width) === "16px") {
    const smallId = `${id}_16`;
    return iconIndex[smallId] ? smallId : id;
  }
  return id;
}

function getViewBox(iconId: string) {
  const dims = iconIndex[iconId];
  if (dims) {
    const [w, h] = dims.split("x").map(Number);
    return [0, 0, w, h].join(" ");
  }
  console.warn(`Unexpected icon ID ${iconId}. This id does not exist.`);
  return "0 0 24 24";
}

const Icon: React.FC<Props> = props => {
  const { id, iconId, title, onClick, disabled = false } = props;

  const _iconId = (legacyNames[iconId] as string) || iconId;
  const resolvedId = resolveIconId(_iconId, props);
  const viewBox = props.viewBox || getViewBox(resolvedId);

  return (
    <IconSvg
      data-testid={id || "icon-svg"}
      {...props}
      iconId={resolvedId}
      viewBox={viewBox}
      onClick={disabled ? null : onClick}
      role="img">
      {title ? <title>{title}</title> : null}
      <use xlinkHref={`#${resolvedId}`} />
    </IconSvg>
  );
};

export default Icon;
